"use client";

import { Button, Close, cn, useIsMobile } from "@oreme/ui";
import { cva } from "class-variance-authority";
import { useAtom } from "jotai";
import { useTranslations, useLocale } from "next-intl";
import React, { useRef, useReducer, type ReactElement } from "react";
import { flushSync } from "react-dom";
import Moveable from "react-moveable";
import { ObservatoryCard, ObservatoryDrawer } from "@/components";
import { activePanelAtom, selectedObservatoryAtom } from "@/store/sites";
import { Plus } from "lucide-react";

const sitesObservatoryPanelVariants = cva(
  "absolute inset-x-2 bottom-3 sm:w-[320px] sm:left-auto sm:right-3 rounded-lg overflow-hidden bg-background/90 hover:bg-background pointer-events-auto transition-background shadow-lg drop-shadow-lg animate__animated animate__fadeInUpBig animate__faster",
  {
    variants: {
      active: {
        true: "z-20",
        false: "z-10",
      },
      visible: {
        true: "flex",
        false: "hidden",
      },
      dragged: {
        true: "z-30",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
      visible: false,
      dragged: false,
    },
  },
);

function SitesPanelObservatory(): ReactElement | null {
  const t = useTranslations("sites.PanelObservatory");
  const locale = useLocale();
  const [activePanel] = useAtom(activePanelAtom);
  const [selectedObservatory, setSelectedObservatory] = useAtom(
    selectedObservatoryAtom,
  );
  const targetRef = useRef<HTMLDivElement>(null);
  const dragTargetRef = useRef<HTMLButtonElement>(null);
  const moveableRef = useRef<Moveable>(null);

  const isMobile = useIsMobile();
  const isActive = activePanel === SitesPanelObservatory.name;
  const isVisible = !!selectedObservatory;
  const [isDragged] = useReducer((state) => {
    return !state;
  }, false);

  let name = null;
  if (selectedObservatory) {
    name = selectedObservatory.name[locale];
  }

  const handleClose = (): void => {
    setSelectedObservatory(null);
  };

  if (isMobile && selectedObservatory) {
    return (
      <div className="absolute z-30 bottom-3 h-8 right-3 rounded-full pointer-events-auto bg-foreground">
        <div className="h-8 flex flex-row items-center justify-between gap-2 pl-2 pr-1 max-w-60">
          {name ? (
            <span className="text-background text-xs min-w-0 truncate">
              {name}
            </span>
          ) : null}
          <span className="flex justify-center items-center w-6 h-6 rounded-full text-foreground border bg-background">
            <Plus strokeWidth={1} />
          </span>
        </div>
        <ObservatoryDrawer observatory={selectedObservatory} />
      </div>
    );
  }

  if (selectedObservatory) {
    return (
      <>
        <div
          className={cn(
            sitesObservatoryPanelVariants({
              active: isActive,
              visible: isVisible,
              dragged: isDragged,
            }),
          )}
          ref={targetRef}
        >
          <header className="absolute z-20 top-0 right-0 flex flex-row justify-between items-start p-2">
            <Button
              onClick={handleClose}
              variant="secondary"
              className="w-5 h-5"
            >
              <Close title={t("close")} />
            </Button>
          </header>
          {selectedObservatory ? (
            <ObservatoryCard
              observatory={selectedObservatory}
              variant="static"
            />
          ) : null}
        </div>
        <Moveable
          dragTarget={dragTargetRef}
          draggable
          edge={false}
          flushSync={flushSync}
          hideDefaultLines
          onRender={(e) => {
            e.target.style.cssText += e.cssText;
          }}
          origin={false}
          ref={moveableRef}
          resizable={false}
          scalable={false}
          target={targetRef}
        />
      </>
    );
  }

  return null;
}

export { SitesPanelObservatory };
